import { useState, useRef } from 'react';
import { MapPin, Calendar, Shield, Camera, Plus, ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ProfileActions from './ProfileActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Image resizing utility
const resizeImage = (file, maxWidth, maxHeight, type = 'avatar') => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      let { width, height } = img;
      
      if (type === 'avatar') {
        const size = Math.min(width, height);
        const sx = (width - size) / 2;
        const sy = (height - size) / 2;
        
        canvas.width = maxWidth;
        canvas.height = maxHeight;
        ctx.drawImage(img, sx, sy, size, size, 0, 0, maxWidth, maxHeight);
      } else {
        const targetRatio = maxWidth / maxHeight;
        const imgRatio = width / height;
        
        let sx = 0, sy = 0, sw = width, sh = height;
        
        if (imgRatio > targetRatio) {
          sw = height * targetRatio;
          sx = (width - sw) / 2;
        } else {
          sh = width / targetRatio;
          sy = (height - sh) / 2;
        }
        
        canvas.width = maxWidth;
        canvas.height = maxHeight;
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, maxWidth, maxHeight);
      }
      
      const quality = 0.85;
      const base64 = canvas.toDataURL('image/jpeg', quality);
      resolve(base64);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

import { getMediaUrl } from '@/utils/mediaUrl';

export const ProfileHeader = ({ profile, isOwner, onProfileUpdate, friendsCount = 0, currentUser, onDevoteesClick }) => {
  const navigate = useNavigate();
  const coverInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  // Check if we should show DOB (based on privacy settings)
  const showDOB = isOwner || profile?.privacy?.show_dob;
  const showLocation = profile?.location;

  // Handle cover photo upload
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }

    setUploadingCover(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const coverUrl = uploadRes.data.media_url || uploadRes.data.url;
      const response = await axios.put(`${API}/user/profile`, { cover_photo: coverUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cover photo updated!');
      if (onProfileUpdate) onProfileUpdate(response.data.user);
    } catch (error) {
      toast.error('Failed to upload cover photo');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }

    setUploadingAvatar(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload/avatar`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile photo updated!');
      if (onProfileUpdate) onProfileUpdate({ ...profile, avatar: uploadRes.data.avatar_url });
    } catch (error) {
      toast.error('Failed to upload profile photo');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const coverPhotoUrl = getMediaUrl(profile?.cover_photo);
  const avatarUrl = getMediaUrl(profile?.avatar || profile?.profile_photo_url);

  return (
    <>
      {/* Cover Photo - 820x312 aspect ratio */}
      <div className="relative">
        <div 
          className="w-full overflow-hidden" 
          style={{ aspectRatio: '820/312', maxHeight: '312px' }}
        >
          {coverPhotoUrl ? (
            <img
              src={coverPhotoUrl}
              alt="Cover"
              className="w-full h-full object-cover"
            />
          ) : (
            /* Clean blank placeholder for cover */
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
              {isOwner ? (
                <button
                  onClick={() => coverInputRef.current?.click()}
                  className="flex flex-col items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={uploadingCover}
                >
                  <div className="w-14 h-14 rounded-full bg-white/80 flex items-center justify-center shadow-sm">
                    {uploadingCover ? (
                      <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6" />
                    )}
                  </div>
                  <span className="text-sm font-medium">Add Cover Photo</span>
                </button>
              ) : (
                <div className="w-14 h-14 rounded-full bg-white/50 flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-gray-300" />
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Edit Cover Button (Owner only, when cover exists) */}
        {isOwner && coverPhotoUrl && (
          <button
            onClick={() => coverInputRef.current?.click()}
            disabled={uploadingCover}
            className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-2 bg-white/90 hover:bg-white text-gray-800 text-sm font-medium rounded-lg shadow-md transition-colors"
            data-testid="edit-cover-btn"
          >
            {uploadingCover ? (
              <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Camera className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Edit Cover Photo</span>
          </button>
        )}

        {/* Hidden file input for cover */}
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          onChange={handleCoverUpload}
          className="hidden"
          data-testid="cover-photo-input"
        />
      </div>

      {/* Profile Info Section */}
      <div className="px-4 sm:px-6 relative">
        {/* Avatar - 168x168, positioned over cover */}
        <div className="relative -mt-16 sm:-mt-20 mb-3">
          <div className="relative inline-block">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-white shadow-xl overflow-hidden bg-gray-100">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={profile?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                /* Clean blank placeholder for avatar */
                <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                  {isOwner ? (
                    <button
                      onClick={() => avatarInputRef.current?.click()}
                      className="w-full h-full flex flex-col items-center justify-center text-gray-400 hover:text-gray-500 transition-colors"
                      disabled={uploadingAvatar}
                    >
                      {uploadingAvatar ? (
                        <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Camera className="w-8 h-8 mb-1" />
                          <span className="text-xs font-medium">Add Photo</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Edit Avatar Button (Owner only, when avatar exists) */}
            {isOwner && avatarUrl && (
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute bottom-2 right-2 w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center shadow-md border-2 border-white transition-colors"
                data-testid="edit-avatar-btn"
              >
                {uploadingAvatar ? (
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 text-gray-700" />
                )}
              </button>
            )}

            {/* Hidden file input for avatar */}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
              data-testid="avatar-photo-input"
            />
          </div>
        </div>

        {/* User Info */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>
              {profile?.name || 'User'}
            </h1>
            {profile?.is_verified && (
              <Shield className="w-5 h-5 text-blue-500" fill="currentColor" />
            )}
            {profile?.role === 'pandit' && (
              <span className="px-3 py-1 bg-[#FF9933] text-white text-xs sm:text-sm rounded-full font-medium">Verified Pandit</span>
            )}
            {profile?.role === 'creator' && (
              <span className="px-3 py-1 bg-purple-500 text-white text-xs sm:text-sm rounded-full font-medium">Creator</span>
            )}
            {profile?.role === 'official' && (
              <span className="px-3 py-1 bg-green-600 text-white text-xs sm:text-sm rounded-full font-medium">Official</span>
            )}
          </div>

          {profile?.username && (
            <p className="text-gray-500 text-sm mt-0.5">@{profile.username}</p>
          )}

          <p className="text-gray-600 mt-2 max-w-2xl text-sm sm:text-base">
            {profile?.bio || 'Devotee on a spiritual journey ॐ'}
          </p>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-gray-500">
            {showLocation && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {profile.location}
              </span>
            )}
            {showDOB && profile?.dob && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(profile.dob)}
              </span>
            )}
            {profile?.created_at && (
              <span className="flex items-center gap-1">
                📅 Joined {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
            )}
          </div>

          {/* Devotees count - mobile view */}
          <div className="lg:hidden mt-3">
            <span className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{friendsCount}</span> {friendsCount === 1 ? 'Devotee' : 'Devotees'}
            </span>
          </div>

          <ProfileActions 
            isOwner={isOwner} 
            profile={profile} 
            currentUser={currentUser}
            friendsCount={friendsCount}
            onDevoteesClick={onDevoteesClick}
          />
        </div>
      </div>
    </>
  );
};

export default ProfileHeader;
